import React, { useState, useMemo, useRef, useEffect } from "react";
import Breadcrums from "../../common/breadcrumbs";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import NoRowsOverlay from "../../common/NoRowsOverlay";
import * as SystemLogsApi from "../../services/systemLogs.service";
import Swal from "sweetalert2";
import { handleServerValidations } from "../../../../utils/commonfunction";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import { Modal, Button, Loader } from "rsuite";

const SystemLogsList = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [category, setCategory] = useState("all");
  const [level, setLevel] = useState("all");
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(false);

  // Selected Log for detail modal
  const [selectedLog, setSelectedLog] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const breadcrumbs = [
    { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
    { title: "System & Cron Logs", url: "" },
  ];

  // Query Stats
  const { data: statsData, refetch: refetchStats } = useQuery({
    queryKey: ["system-log-stats"],
    queryFn: () => SystemLogsApi.getSystemLogStats(),
    refetchOnWindowFocus: false,
    retry: false,
  });

  // Query Logs
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["system-logs", page, limit, category, level, search, startDate, endDate],
    queryFn: () => SystemLogsApi.getSystemLogs(page, limit, category, level, search, startDate, endDate),
    refetchOnWindowFocus: false,
    retry: false,
    keepPreviousData: true,
    onError: (error) => {
      Swal.fire({
        icon: "error",
        text: handleServerValidations(error),
        ...SWAL_SETTINGS,
      });
    },
  });

  // Auto-refresh logic
  useEffect(() => {
    let interval = null;
    if (autoRefresh) {
      interval = setInterval(() => {
        refetch();
        refetchStats();
      }, 10000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, refetch, refetchStats]);

  const stats = statsData?.data || {};
  const logsResponse = data?.data || {};
  const logList = logsResponse.logs || [];
  const totalCount = logsResponse.totalCount || 0;
  const totalPages = logsResponse.totalPages || 1;

  const handleDownload = async () => {
    try {
      const response = await SystemLogsApi.downloadSystemLogs(category, level, search, startDate, endDate);
      const blob = new Blob([response.data], { type: "text/plain" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `system-logs-${category}-${Date.now()}.log`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      Swal.fire({
        icon: "error",
        text: handleServerValidations(err),
        ...SWAL_SETTINGS,
      });
    }
  };

  const getLevelBadge = (lvl) => {
    switch (lvl) {
      case "ERROR":
        return <span className="badge badge-danger">ERROR</span>;
      case "WARN":
        return <span className="badge badge-warning text-dark">WARN</span>;
      case "DEBUG":
        return <span className="badge badge-info">DEBUG</span>;
      case "INFO":
      default:
        return <span className="badge badge-success">INFO</span>;
    }
  };

  const getCategoryBadge = (cat) => {
    switch (cat) {
      case "cron":
        return <span className="badge badge-primary-light"><i className="fa fa-clock-o mr-1"></i>Cron Job</span>;
      case "domain":
        return <span className="badge badge-info-light"><i className="fa fa-globe mr-1"></i>Domain/API</span>;
      default:
        return <span className="badge badge-secondary-light"><i className="fa fa-cog mr-1"></i>System</span>;
    }
  };

  return (
    <div>
      <Breadcrums breadcrumbs={breadcrumbs} />

      {/* Stats Cards */}
      <div className="row row-sm mb-4">
        <div className="col-xl-3 col-lg-6 col-md-6 col-xm-12">
          <div className="card custom-card">
            <div className="card-body iconfont">
              <div className="d-flex justify-content-between">
                <div>
                  <p className="mb-1 text-muted">Total Logs Scanned</p>
                  <h3 className="mb-0 font-weight-bold">{stats.totalScanned || 0}</h3>
                </div>
                <div className="card-icon text-primary bg-primary-transparent rounded-circle p-3">
                  <i className="fa fa-list-alt fa-2x"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-lg-6 col-md-6 col-xm-12">
          <div
            className={`card custom-card cursor-pointer ${category === "cron" ? "border-primary" : ""}`}
            onClick={() => { setCategory(category === "cron" ? "all" : "cron"); setPage(1); }}
            style={{ cursor: "pointer" }}
          >
            <div className="card-body iconfont">
              <div className="d-flex justify-content-between">
                <div>
                  <p className="mb-1 text-muted">Cron Worker Logs</p>
                  <h3 className="mb-0 font-weight-bold text-primary">{stats.cronCount || 0}</h3>
                </div>
                <div className="card-icon text-primary bg-primary-transparent rounded-circle p-3">
                  <i className="fa fa-clock-o fa-2x"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-lg-6 col-md-6 col-xm-12">
          <div
            className={`card custom-card cursor-pointer ${category === "domain" ? "border-info" : ""}`}
            onClick={() => { setCategory(category === "domain" ? "all" : "domain"); setPage(1); }}
            style={{ cursor: "pointer" }}
          >
            <div className="card-body iconfont">
              <div className="d-flex justify-content-between">
                <div>
                  <p className="mb-1 text-muted">Domain / API Logs</p>
                  <h3 className="mb-0 font-weight-bold text-info">{stats.domainCount || 0}</h3>
                </div>
                <div className="card-icon text-info bg-info-transparent rounded-circle p-3">
                  <i className="fa fa-globe fa-2x"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-lg-6 col-md-6 col-xm-12">
          <div
            className={`card custom-card cursor-pointer ${category === "error" ? "border-danger" : ""}`}
            onClick={() => { setCategory(category === "error" ? "all" : "error"); setPage(1); }}
            style={{ cursor: "pointer" }}
          >
            <div className="card-body iconfont">
              <div className="d-flex justify-content-between">
                <div>
                  <p className="mb-1 text-muted">Error Logs</p>
                  <h3 className="mb-0 font-weight-bold text-danger">{stats.errorCount || 0}</h3>
                </div>
                <div className="card-icon text-danger bg-danger-transparent rounded-circle p-3">
                  <i className="fa fa-exclamation-triangle fa-2x"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Log Table Card */}
      <div className="row row-sm">
        <div className="col-lg-12">
          <div className="card custom-card">
            <div className="card-body">
              {/* Filter Controls Header */}
              <div className="d-flex flex-wrap align-items-center justify-content-between mb-3 gap-2">
                {/* Category Pills */}
                <div className="btn-group mb-2" role="group">
                  <button
                    type="button"
                    className={`btn btn-${category === "all" ? "primary" : "outline-primary"}`}
                    onClick={() => { setCategory("all"); setPage(1); }}
                  >
                    All Logs
                  </button>
                  <button
                    type="button"
                    className={`btn btn-${category === "cron" ? "primary" : "outline-primary"}`}
                    onClick={() => { setCategory("cron"); setPage(1); }}
                  >
                    <i className="fa fa-clock-o mr-1"></i> Cron Jobs
                  </button>
                  <button
                    type="button"
                    className={`btn btn-${category === "domain" ? "primary" : "outline-primary"}`}
                    onClick={() => { setCategory("domain"); setPage(1); }}
                  >
                    <i className="fa fa-globe mr-1"></i> Domain & API
                  </button>
                  <button
                    type="button"
                    className={`btn btn-${category === "error" ? "danger" : "outline-danger"}`}
                    onClick={() => { setCategory("error"); setPage(1); }}
                  >
                    <i className="fa fa-exclamation-circle mr-1"></i> Errors
                  </button>
                </div>

                {/* Right Action Buttons */}
                <div className="d-flex align-items-center mb-2 gap-2">
                  <div className="custom-control custom-switch mr-3">
                    <input
                      type="checkbox"
                      className="custom-control-input"
                      id="autoRefreshSwitch"
                      checked={autoRefresh}
                      onChange={(e) => setAutoRefresh(e.target.checked)}
                    />
                    <label className="custom-control-label cursor-pointer" htmlFor="autoRefreshSwitch">
                      Auto Refresh (10s)
                    </label>
                  </div>

                  <button
                    className="btn btn-outline-secondary btn-sm mr-2"
                    onClick={() => { refetch(); refetchStats(); }}
                    title="Refresh logs"
                  >
                    <i className={`fa fa-refresh ${isFetching ? "fa-spin" : ""}`}></i> Refresh
                  </button>

                  <button className="btn btn-success btn-sm" onClick={handleDownload}>
                    <i className="fa fa-download mr-1"></i> Download Logs
                  </button>
                </div>
              </div>

              {/* Second Row: Filters & Search */}
              <div className="row mb-3">
                <div className="col-md-3 mb-2">
                  <label className="form-label font-weight-bold">Log Level</label>
                  <select
                    className="form-control"
                    value={level}
                    onChange={(e) => { setLevel(e.target.value); setPage(1); }}
                  >
                    <option value="all">All Levels</option>
                    <option value="INFO">INFO</option>
                    <option value="ERROR">ERROR</option>
                    <option value="WARN">WARN</option>
                    <option value="DEBUG">DEBUG</option>
                  </select>
                </div>

                <div className="col-md-4 mb-2">
                  <label className="form-label font-weight-bold">Search Keywords</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search logs by keyword, endpoint, or cron job..."
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  />
                </div>

                <div className="col-md-2 mb-2">
                  <label className="form-label font-weight-bold">Start Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={startDate}
                    onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
                  />
                </div>

                <div className="col-md-2 mb-2">
                  <label className="form-label font-weight-bold">End Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={endDate}
                    onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
                  />
                </div>

                <div className="col-md-1 mb-2 d-flex align-items-end">
                  <button
                    className="btn btn-light w-100"
                    onClick={() => {
                      setSearch("");
                      setLevel("all");
                      setCategory("all");
                      setStartDate("");
                      setEndDate("");
                      setPage(1);
                    }}
                    title="Reset Filters"
                  >
                    Reset
                  </button>
                </div>
              </div>

              {/* Log Console Table */}
              {isLoading ? (
                <div className="text-center py-5">
                  <Loader size="md" content="Loading system & cron logs..." vertical />
                </div>
              ) : logList.length === 0 ? (
                <NoRowsOverlay message="No logs found matching your selected filters." />
              ) : (
                <div className="table-responsive" style={{ backgroundColor: "#1e1e1e", borderRadius: "8px", padding: "12px" }}>
                  <table className="table table-dark table-hover mb-0" style={{ fontSize: "13px", fontFamily: "monospace" }}>
                    <thead>
                      <tr style={{ color: "#888", borderBottom: "1px solid #333" }}>
                        <th style={{ width: "180px" }}>Timestamp</th>
                        <th style={{ width: "80px" }}>Level</th>
                        <th style={{ width: "130px" }}>Category</th>
                        <th style={{ width: "140px" }}>Tag/Module</th>
                        <th>Message</th>
                        <th style={{ width: "70px" }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logList.map((log, idx) => (
                        <tr key={log.id || idx} style={{ borderBottom: "1px solid #2a2a2a" }}>
                          <td style={{ color: "#aaa" }}>
                            {log.timestamp ? new Date(log.timestamp).toLocaleString() : "N/A"}
                          </td>
                          <td>{getLevelBadge(log.level)}</td>
                          <td>{getCategoryBadge(log.category)}</td>
                          <td>
                            <span className="badge badge-outline-secondary" style={{ color: "#00d2ff", borderColor: "#00d2ff" }}>
                              {log.tag}
                            </span>
                          </td>
                          <td
                            style={{
                              color: log.level === "ERROR" ? "#ff6b6b" : log.level === "WARN" ? "#ffd166" : "#e0e0e0",
                              wordBreak: "break-word",
                              whiteSpace: "pre-wrap"
                            }}
                          >
                            {log.message}
                          </td>
                          <td>
                            <button
                              className="btn btn-xs btn-outline-info p-1"
                              onClick={() => { setSelectedLog(log); setModalOpen(true); }}
                              title="View details"
                            >
                              <i className="fa fa-eye"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination Footer */}
              <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap">
                <div className="d-flex align-items-center mb-2">
                  <span className="mr-2 text-muted">Show:</span>
                  <select
                    className="form-control form-control-sm"
                    style={{ width: "80px" }}
                    value={limit}
                    onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                  >
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={200}>200</option>
                  </select>
                  <span className="ml-3 text-muted">
                    Showing {(page - 1) * limit + 1} - {Math.min(page * limit, totalCount)} of {totalCount} logs
                  </span>
                </div>

                <div className="mb-2">
                  <button
                    className="btn btn-outline-secondary btn-sm mr-2"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(p - 1, 1))}
                  >
                    Previous
                  </button>
                  <span className="mx-2 text-muted">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    className="btn btn-outline-secondary btn-sm ml-2"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Log Detail Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} size="lg">
        <Modal.Header>
          <Modal.Title>Log Entry Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedLog && (
            <div>
              <div className="row mb-3">
                <div className="col-md-4">
                  <strong>Timestamp:</strong>
                  <div>{selectedLog.timestamp ? new Date(selectedLog.timestamp).toLocaleString() : "N/A"}</div>
                </div>
                <div className="col-md-3">
                  <strong>Level:</strong>
                  <div>{getLevelBadge(selectedLog.level)}</div>
                </div>
                <div className="col-md-3">
                  <strong>Category:</strong>
                  <div>{getCategoryBadge(selectedLog.category)}</div>
                </div>
                <div className="col-md-2">
                  <strong>Tag:</strong>
                  <div><span className="badge badge-info">{selectedLog.tag}</span></div>
                </div>
              </div>

              <div className="form-group">
                <label className="font-weight-bold">Parsed Message:</label>
                <div
                  style={{
                    backgroundColor: "#1e1e1e",
                    color: selectedLog.level === "ERROR" ? "#ff6b6b" : "#e0e0e0",
                    padding: "12px",
                    borderRadius: "6px",
                    fontFamily: "monospace",
                    fontSize: "13px",
                    whiteSpace: "pre-wrap",
                    maxHeight: "300px",
                    overflowY: "auto"
                  }}
                >
                  {selectedLog.message}
                </div>
              </div>

              <div className="form-group">
                <label className="font-weight-bold">Raw Log Output:</label>
                <div
                  style={{
                    backgroundColor: "#2a2a2a",
                    color: "#888",
                    padding: "12px",
                    borderRadius: "6px",
                    fontFamily: "monospace",
                    fontSize: "12px",
                    whiteSpace: "pre-wrap",
                    maxHeight: "150px",
                    overflowY: "auto"
                  }}
                >
                  {selectedLog.raw}
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setModalOpen(false)} appearance="subtle">
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SystemLogsList;
